/**
 * MatchCard - the shared frame for every "matching" surface (the founder's
 * "basis and design direction of any matching design"). Ported from the Figma
 * matches card (meetball_design_system node 6960:14741): a soft rounded card with
 * the multi-hue pastel wash, a leading brand mark + title header, and translucent
 * pill rows as `children`.
 *
 * Composition, not booleans (the house rule): the card owns ONLY the static frame
 * (rounded-10 offwhite surface + shadow, header, padding). Every animated layer -
 * the wash bloom, the border-light revolution, the arrival ripple - is passed in
 * via the `accent` slot and painted BEHIND the content, so each surface keeps
 * owning its own choreography (MatchReveal, ForYou). A surface that wants the
 * resting look with no motion drops in `<MatchCardWash />` (SuggestedHelpers); a
 * surface mid-load (MatchReveal's searching interim) passes no accent and reads as
 * the calm offwhite base until its faces bloom in.
 *
 * NOT overflow-hidden: the border-light / ripple accents sit at `inset:-1px`, so
 * the card must not clip them (the wash layers self-clip via their own radius).
 */
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '@lib/utils';

/** Served from `public/` as a static asset - the two-part hand+sparkle composite
 * exported from Figma as-is. */
const MATCH_CARD_MARK_SRC = '/match-card-mark.svg';

/** The matches brand mark (helping hand + sparkles), 34x28. Decorative - the
 * accessible name comes from the adjacent title. */
export function MatchCardMark({ className }: { className?: string }) {
  return (
    <img
      src={MATCH_CARD_MARK_SRC}
      alt=""
      aria-hidden="true"
      className={cn('h-7 w-[34px] shrink-0', className)}
    />
  );
}

/**
 * The canonical matches-card title (the founder's copy, verbatim). ONE string
 * for every "matching" surface - ForYouSection's event-page card, MatchReveal's
 * async reveal, and the wrapped SuggestedHelpersSection all import this instead
 * of carrying their own literal, so the surfaces can't drift apart again.
 */
export const MATCH_CARD_TITLE = 'Your meetball matches';

/** Group eyebrow titles: helpers Going to the linked live event vs the same-spaces network.
 * Shared by SuggestedHelpersSection and MatchReveal so both surfaces label groups identically
 * (fb5 item 3). */
export const EVENT_GROUP_TITLE = 'At the event';
/** The network eyebrow is viewer-relative (fb5 C11): "YOUR SPACES" is only true when the viewer is
 * the asker. A bystander looking at someone else's matches sees the asker's spaces, so the
 * possessive flips to "THEIR". Picked by MatchesCard from `isAsker`. */
export const NETWORK_GROUP_TITLE = 'PEOPLE WHO CAN HELP FROM YOUR SPACES';
export const NETWORK_GROUP_TITLE_BYSTANDER = 'PEOPLE WHO CAN HELP FROM THEIR SPACES';

/** The resting (non-animated) wash layer: the shared pastel mesh (the `bg-match-wash`
 * theme-token utility - single source), self-clipped to the card radius. Drop it into
 * `MatchCard`'s `accent` slot for surfaces with no load choreography. */
export function MatchCardWash({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'bg-match-wash pointer-events-none absolute inset-0 z-0 rounded-[10px]',
        className,
      )}
    />
  );
}

/**
 * The uppercase eyebrow that heads a helper group - EVENT_GROUP_TITLE (event attendees) or
 * NETWORK_GROUP_TITLE (the same-spaces network). Shared by SuggestedHelpersSection and MatchReveal
 * so the two surfaces label groups identically; the muted eyebrow reads cleanly over the
 * MatchCardWash (fb5 item 3).
 */
export function MatchGroupLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h4
      className={cn(
        'text-mb-grey-400 mb-1.5 text-xs font-medium tracking-[0.24px] uppercase',
        className,
      )}
    >
      {children}
    </h4>
  );
}

/**
 * The shared teal->periwinkle gradient avatar ring (node 6960:14741's stroke,
 * confirmed on the root frame's gradientStops and the brand-mark sparkle defs): a
 * `ring` box-shadow can't carry a gradient, so the ring is a padding wrapper
 * (`rounded-full` + `p-[1.5px]` + the gradient background) with the avatar nested
 * inside - the padding IS the ring thickness. ONE definition reused by every
 * matching-surface avatar (HelperListItem's grid-placed avatar, ForYouSection's
 * flex-placed avatar) so a future ring restyle (color, thickness) can't silently
 * diverge between them (K4). `className` carries the CALLER's positioning (grid
 * placement vs `shrink-0`) - the ring itself owns only the gradient treatment.
 */
export function MatchAvatarRing({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-full bg-linear-to-br from-mb-teal to-mb-periwinkle-soft p-[1.5px]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface MatchCardProps extends Omit<ComponentPropsWithRef<'section'>, 'title'> {
  /** Header title, 16px semibold (Figma "Your best matches at this event"). */
  title: string;
  /** Optional second header line under the title (e.g. SuggestedHelpers' "At {event}"). */
  subtitle?: ReactNode;
  /** Header leading glyph; defaults to the shared brand mark. */
  icon?: ReactNode;
  /** Header trailing slot (an info glyph in the design). Empty on our surfaces -
   * the slot exists so a future affordance has a home, but no dead control ships. */
  trailing?: ReactNode;
  /** Wash / choreography layers, painted BEHIND the content at z-0. */
  accent?: ReactNode;
  children: ReactNode;
}

export function MatchCard({
  title,
  subtitle,
  icon,
  trailing,
  accent,
  children,
  className,
  ref,
  ...sectionProps
}: MatchCardProps) {
  return (
    <section
      ref={ref}
      className={cn('relative rounded-[10px] bg-mb-offwhite px-2 py-3.5 shadow-soft', className)}
      {...sectionProps}
    >
      {accent}
      <div className="relative z-10 flex flex-col gap-3.5">
        <header className="flex items-center gap-2">
          {icon ?? <MatchCardMark />}
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-semibold text-[#1c1c1c]">{title}</h3>
            {subtitle ? <p className="text-sm text-[#6b6b6b]">{subtitle}</p> : null}
          </div>
          {trailing ? <div className="shrink-0">{trailing}</div> : null}
        </header>
        {children}
      </div>
    </section>
  );
}
